import { Link, useLocation } from "wouter";
import { useAuthUser } from "@/App";
import { 
  Tags, 
  Search, 
  Menu,
  Moon,
  Sun,
  ChevronLeft,
  LayoutTemplate,
  TrendingUp,
  MessageSquareText,
  BarChart3,
  LayoutGrid,
  ChevronDown,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { AccountSwitcher } from "@/components/meta/AccountSwitcher";

const AD_ACCOUNTS_NAV_ITEMS = [
  { icon: BarChart3, label: "Dashboard", href: "/dashboard" },
  { icon: LayoutGrid, label: "Ads Manager", href: "/ads-manager" },
  { icon: MessageSquareText, label: "AskMoji", href: "/askmoji" },
  { icon: Activity, label: "Heartbeat", href: "/heartbeat" },
];

const CREATIVE_NAV_ITEMS = [
  { icon: Tags, label: "Brands", href: "/brands" },
  { icon: Search, label: "Find Ads", href: "/find-ads" },
  { icon: LayoutTemplate, label: "Ad Templates", href: "/ad-templates" },
];

const INSIGHTS_NAV_ITEMS = [
  { icon: TrendingUp, label: "Organic Posts", href: "/organic-posts" },
];

type SectionKey = "adAccounts" | "creative" | "insights";

function getSavedSections(): Record<SectionKey, boolean> {
  try {
    const saved = localStorage.getItem('sidebar-sections');
    if (saved) return JSON.parse(saved);
  } catch {}
  return { adAccounts: true, creative: true, insights: true };
}

function saveSections(sections: Record<SectionKey, boolean>) {
  localStorage.setItem('sidebar-sections', JSON.stringify(sections));
}

export function Layout({ children }: { children: React.ReactNode }) {
  const user = useAuthUser();
  const [location] = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(getSavedSections);

  const toggleCollapsed = () => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('sidebar-collapsed', String(newValue));
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 310);
      return newValue;
    });
  };

  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveSections(next);
      return next;
    });
  }, []);

  const sidebarWidth = isCollapsed ? "w-16" : "w-[260px]";
  const mobileSidebarWidth = "w-16";
  const contentPadding = isCollapsed ? "md:pl-16" : "md:pl-[260px]";

  const isItemActive = (href: string) => {
    if (href === "/") return location === "/" || location === "/meta/dashboard";
    return location === href || location.startsWith(href + "/");
  };

  const NavItem = ({ item, collapsed }: { item: { icon: any; label: string; href: string }; collapsed: boolean }) => {
    const isActive = isItemActive(item.href);
    const isHeartbeat = item.href === "/heartbeat";
    return (
      <Link href={item.href} className={cn(
        "flex items-center rounded-lg py-2.5 text-sm transition-all duration-200 cursor-pointer group select-none",
        collapsed ? "justify-center px-2" : "gap-3 px-3",
        isActive
          ? "bg-sidebar-accent text-sidebar-accent-foreground"
          : "text-sidebar-foreground hover:text-white"
      )} title={collapsed ? item.label : undefined}>
        <span className="relative flex-shrink-0">
          <item.icon className={cn(
            "h-5 w-5 stroke-[1.5]",
            isActive ? "text-white" : isHeartbeat ? "text-indigo-400 group-hover:text-indigo-300" : "text-gray-500 group-hover:text-gray-300"
          )} />
        </span>
        {!collapsed && item.label}
      </Link>
    );
  };

  const SectionHeader = ({ label, sectionKey, collapsed }: { label: string; sectionKey: SectionKey; collapsed: boolean }) => {
    if (collapsed) return null;
    const isOpen = openSections[sectionKey];
    return (
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full px-3 mb-1 group cursor-pointer"
        data-testid={`section-toggle-${sectionKey}`}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 group-hover:text-gray-400 transition-colors">
          {label}
        </span>
        <ChevronDown className={cn(
          "h-3 w-3 text-gray-600 transition-transform duration-200",
          !isOpen && "-rotate-90"
        )} />
      </button>
    );
  };

  const SidebarContent = ({ collapsed = false, showToggle = true }: { collapsed?: boolean; showToggle?: boolean }) => (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground font-medium">
      <div className={cn("flex h-14 items-center pt-4 pb-2", collapsed ? "justify-center px-2" : "justify-between px-5")}>
        {!collapsed && <img src="/admoji-logo-white.png" alt="admoji" className="h-7" />}
        {showToggle && (
          collapsed ? (
            <button
              data-testid="button-toggle-sidebar"
              onClick={toggleCollapsed}
              className="p-1 rounded hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-gray-500 hover:text-white cursor-pointer" />
            </button>
          ) : (
            <button
              data-testid="button-toggle-sidebar"
              onClick={toggleCollapsed}
              className="p-1 rounded hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-gray-500 hover:text-white cursor-pointer" />
            </button>
          )
        )}
      </div>

      {!collapsed && (
        <div className="px-3 mb-2">
          <Link href="/profile">
           <div className="flex items-center justify-between group p-2 hover:bg-white/5 rounded-lg cursor-pointer transition-colors" data-testid="link-profile-sidebar">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-7 w-7 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-indigo-400 text-xs font-semibold">{user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "?"}</span>
                </div>
                <span className="text-gray-300 font-medium text-sm truncate">{user?.email || "Profile"}</span>
              </div>
           </div>
          </Link>
        </div>
      )}

      {collapsed && (
        <div className="px-2 mb-2 flex justify-center">
          <Link href="/profile">
            <div className="p-2 hover:bg-white/5 rounded-lg cursor-pointer transition-colors" data-testid="link-profile-sidebar-collapsed">
              <div className="h-7 w-7 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <span className="text-indigo-400 text-xs font-semibold">{user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "?"}</span>
              </div>
            </div>
          </Link>
        </div>
      )}

      <div className={cn("flex-1 overflow-y-auto py-2 custom-scrollbar space-y-4", collapsed ? "px-2" : "px-3")}>
        <div>
          <SectionHeader label="Ad Accounts" sectionKey="adAccounts" collapsed={collapsed} />
          {!collapsed && <AccountSwitcher />}
          {(collapsed || openSections.adAccounts) && (
            <div className={cn("space-y-1", !collapsed && "mt-2")}>
              {AD_ACCOUNTS_NAV_ITEMS.map((item) => (
                <NavItem key={item.href} item={item} collapsed={collapsed} />
              ))}
            </div>
          )}
        </div>

        <div className={cn(collapsed ? "pt-3 border-t border-gray-700/50" : "")}>
          <SectionHeader label="Creative" sectionKey="creative" collapsed={collapsed} />
          {(collapsed || openSections.creative) && (
            <div className={cn("space-y-1", !collapsed && "mt-1")}>
              {CREATIVE_NAV_ITEMS.map((item) => (
                <NavItem key={item.href} item={item} collapsed={collapsed} />
              ))}
            </div>
          )}
        </div>

        <div className={cn(collapsed ? "pt-3 border-t border-gray-700/50" : "")}>
          <SectionHeader label="Insights" sectionKey="insights" collapsed={collapsed} />
          {(collapsed || openSections.insights) && (
            <div className={cn("space-y-1", !collapsed && "mt-1")}>
              {INSIGHTS_NAV_ITEMS.map((item) => (
                <NavItem key={item.href} item={item} collapsed={collapsed} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={cn("space-y-4", collapsed ? "p-2" : "p-4")}>
        {!collapsed ? (
          <div className="flex items-center bg-[#27272A] rounded-full p-1 h-10">
             <button className="flex-1 flex items-center justify-center gap-2 h-full text-xs font-medium text-white bg-gray-600/50 rounded-full shadow-sm transition-all">
               <Sun className="h-4 w-4" /> Light
             </button>
             <button className="flex-1 flex items-center justify-center gap-2 h-full text-xs font-medium text-gray-400 hover:text-white transition-all">
               <Moon className="h-4 w-4" /> Dark
             </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <button className="p-2 rounded-lg bg-gray-600/50 text-white" title="Light mode">
              <Sun className="h-4 w-4" />
            </button>
          </div>
        )}

        <Link href="/profile" className={cn("flex items-center pt-2 hover:opacity-80 transition-opacity cursor-pointer", collapsed ? "justify-center" : "gap-3")} data-testid="link-profile">
          <div className="h-10 w-10 rounded-full bg-[#9D174D] flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
            {user ? user.name.split(" ").map(n => n[0]).join("") : "U"}
          </div>
          {!collapsed && (
            <>
              <div className="flex flex-col overflow-hidden">
                <span className="truncate text-sm font-semibold text-white">{user?.name ?? "User"}</span>
                <span className="truncate text-xs text-gray-500">Profile</span>
              </div>
              <div className="ml-auto">
                 <div className="flex flex-col gap-0.5">
                    <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                    <div className="h-1 w-1 bg-gray-500 rounded-full"></div>
                 </div>
              </div>
            </>
          )}
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-sans">
      {/* Mobile Sidebar — always collapsed, no toggle */}
      <aside className={cn("fixed inset-y-0 left-0 z-50 md:hidden bg-sidebar border-r border-sidebar-border", mobileSidebarWidth)}>
        <SidebarContent collapsed={true} showToggle={false} />
      </aside>

      {/* Desktop Sidebar — user-controlled collapsed/expanded */}
      <aside className={cn("fixed inset-y-0 left-0 z-50 hidden md:block bg-sidebar border-r border-sidebar-border transition-all duration-300 ease-in-out", sidebarWidth)}>
        <SidebarContent collapsed={isCollapsed} />
      </aside>

      {/* Mobile Drawer */}
      <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
        <SheetContent side="left" className="p-0 w-[260px] border-r-0 bg-sidebar">
           <SidebarContent collapsed={false} showToggle={false} />
        </SheetContent>
      </Sheet>

      <div className={cn(contentPadding, "flex flex-col min-h-screen transition-all duration-300 ease-in-out w-full overflow-x-hidden")}>
        <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-gray-500"
            onClick={() => setIsMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>

        <main className="flex-1 min-w-0 p-4 md:p-8 animate-in fade-in duration-500 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
}
